import { cn } from "@/components/ui/utils";

/**
 * How a list's rows look, in three words the list says once and its rows
 * read from it (``SelectableListStyleContext``):
 *
 * - ``size``: how much room a row takes and what its text is set at.
 * - ``variant``: what the selected row draws round its surface - ``plain``
 *   nothing; ``soft`` nothing either, but its rows take the surface under the
 *   pointer too; ``outlined`` a line round it and a lift. The surface itself
 *   is the one colour for all (``SELECTABLE_ROW_SELECTED_BG``), except an
 *   outlined card, which lifts to the ground (``SELECTABLE_CARD_SELECTED_BG``).
 * - ``layout``: ``flush`` runs the rows edge to edge with a hairline between
 *   each, kept beside the selected one; ``cards`` insets them, rounds them,
 *   and drops the hairlines beside the selected card, whose surface has its
 *   own edges.
 *
 * Hover always underlines the title (via ``data-selectable-list-title``)
 * and, by default, gives the row a surface as well. A list can say its rows
 * keep still (``rowHover="title"``): under it the underline is all a hovered
 * row does, because a row that fills under a passing pointer reads as chosen.
 */

export type SelectableListSize = "xs" | "sm" | "md" | "lg";
export type SelectableListVariant = "plain" | "soft" | "outlined";
export type SelectableListLayout = "flush" | "cards";

export type SelectableListStyle = {
  size: SelectableListSize;
  variant: SelectableListVariant;
  layout: SelectableListLayout;
  /** The rows draw themselves: the list's wrapper carries no inset or chrome. */
  rowChromeless: boolean;
};

export const SELECTABLE_LIST_STYLE_DEFAULT: SelectableListStyle = {
  size: "md",
  variant: "soft",
  layout: "cards",
  rowChromeless: false,
};

/** A row's inset for each size, in spacing units. */
export const SELECTABLE_LIST_ROW_INSET: Record<
  SelectableListSize,
  { px: number; py: number }
> = {
  xs: { px: 1, py: 0.5 },
  sm: { px: 1.5, py: 1.5 },
  md: { px: 2, py: 2 },
  lg: { px: 2.5, py: 2.5 },
};

/** The list's own inset round its cards, by size; a flush list has none. */
export const SELECTABLE_LIST_INSET: Record<SelectableListSize, number> = {
  xs: 0.5,
  sm: 1.5,
  md: 1.5,
  lg: 1,
};

/**
 * A card's corners, by the list's size: the theme's radius each one draws
 * with, rounder as the list's cards get roomier.
 */
export const SELECTABLE_CARD_RADIUS: Record<
  SelectableListSize,
  "sm" | "md" | "lg" | "xl"
> = {
  xs: "md",
  sm: "lg",
  md: "xl",
  lg: "xl",
};

/**
 * The inset a row's content sits in, for the list's size and layout: as a
 * card, the row's own; flush, that plus what the list would have put round a
 * card (its inset, and the half unit a card stands in from its edge), so
 * content starts at the same pixel whichever layout the list is in.
 */
export function getSelectableRowInset(
  size: SelectableListSize,
  layout: SelectableListLayout,
): { px: number; py: number } {
  const inset = SELECTABLE_LIST_ROW_INSET[size];
  if (layout === "cards") return inset;
  const listInset = SELECTABLE_LIST_INSET[size];
  return { px: inset.px + listInset + 0.5, py: inset.py + listInset };
}

/**
 * ``getSelectableRowInset`` as classes, by layout and size: the inset the
 * list's own rows sit in.
 */
export const SELECTABLE_ROW_INSET_CLASS: Record<
  SelectableListLayout,
  Record<SelectableListSize, string>
> = {
  cards: {
    xs: "pl-2 pr-2 pt-1 pb-1",
    sm: "pl-3 pr-3 pt-3 pb-3",
    md: "pl-4 pr-4 pt-4 pb-4",
    lg: "pl-5 pr-5 pt-5 pb-5",
  },
  flush: {
    xs: "pl-4 pr-4 pt-2 pb-2",
    sm: "pl-7 pr-7 pt-6 pb-6",
    md: "pl-8 pr-8 pt-7 pb-7",
    lg: "pl-8 pr-8 pt-7 pb-7",
  },
};

/** ``SELECTABLE_LIST_INSET`` as padding all round, by size. */
export const SELECTABLE_LIST_INSET_CLASS: Record<SelectableListSize, string> = {
  xs: "p-1",
  sm: "p-3",
  md: "p-3",
  lg: "p-2",
};

/** A card's corners as a class, by the theme's radius (``SELECTABLE_CARD_RADIUS``). */
export const SELECTABLE_CARD_RADIUS_CLASS: Record<
  (typeof SELECTABLE_CARD_RADIUS)[SelectableListSize],
  string
> = {
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
};

/**
 * Where a list that holds rows puts what it stands on, for what it covers the
 * held ones' surroundings in.
 */
export const SELECTABLE_ROW_GROUND_VAR = "--selectable-row-ground";

/** What a list's rows do under the pointer; the list writes it on its root. */
export type SelectableRowHover = "surface" | "title";
export const SELECTABLE_ROW_HOVER_ATTR = "data-selectable-row-hover";

/**
 * The keyboard's ring round the row it is on - mixed into the ground the list
 * gives (``SELECTABLE_ROW_GROUND_VAR``), so it is solid, and where it is drawn
 * twice it is no darker - or the transparent one every row draws so it shifts
 * nothing.
 */
export const SELECTABLE_ROW_RING_CLASS = {
  on: "[outline:2px_solid_color-mix(in_srgb,var(--muted-foreground)_14%,var(--selectable-row-ground,transparent)_86%)]",
  off: "[outline:2px_solid_transparent]",
} as const;

/** Hovered, a row's title is underlined. */
const SELECTABLE_ROW_TITLE_HOVER_CLASS =
  "[&:hover_[data-selectable-list-title]]:underline [&:hover_[data-selectable-list-title]]:[text-decoration-thickness:initial] [&:hover_[data-selectable-list-title]]:[text-decoration-style:initial] [&:hover_[data-selectable-list-title]]:decoration-[currentColor] [&:hover_[data-selectable-list-title]]:underline-offset-2";

/**
 * The row's surface for its variant and layout, selected or not, as classes.
 * The row always draws its full box (a transparent border, say) so selecting
 * one does not shift the list. Under the pointer it takes the surface it has
 * chosen - unless the list it is in keeps its rows still
 * (``SELECTABLE_ROW_HOVER_ATTR`` "title").
 *
 * A chosen row has one surface, whatever its variant and layout - the content
 * surface's next step, ``background.level1`` - the variants differing in what
 * else they draw round it. An outlined *card* takes the app's own ground,
 * ``background.body``, instead: a list of cards is drawn on the content
 * surface, already a step off that ground, so a card tinting a step the same
 * way reads as nothing. A card lifts off its canvas rather than sinking into
 * it.
 */
export function getSelectableRowVariantClassName({
  size,
  variant,
  layout,
  isActive,
  isFocusWithin,
  hasHoverSurface = true,
  isEveryRowCard = false,
}: {
  size: SelectableListSize;
  variant: SelectableListVariant;
  layout: SelectableListLayout;
  isActive: boolean;
  /** The keys are in the list: the chosen row says so (a card by its line and lift, any other by its ring). */
  isFocusWithin: boolean;
  /** Under the pointer, the row takes its surface; its title is underlined either way. */
  hasHoverSurface?: boolean;
  /** Every row a card, as the one chosen is (``SelectableListStyle``'s). */
  isEveryRowCard?: boolean;
}): string {
  const cards = layout === "cards";
  const base = cn(
    "relative box-border",
    cards
      ? SELECTABLE_CARD_RADIUS_CLASS[SELECTABLE_CARD_RADIUS[size]]
      : "rounded-none",
    "[transition:background-color_120ms_ease,box-shadow_120ms_ease,border-color_120ms_ease]",
    SELECTABLE_ROW_TITLE_HOVER_CLASS,
  );

  if (variant === "plain" || variant === "soft") {
    // Nothing drawn round the surface.
    return cn(
      base,
      isActive ? "bg-muted" : "bg-transparent",
      hasHoverSurface &&
        "[&:hover:not([data-selectable-row-hover='title']_*)]:bg-muted",
    );
  }

  // outlined
  if (cards) {
    // Chosen, it is a card on the app's ground, outlined in the hairlines'
    // colour - in a list whose every row is a card (the queue's), as each
    // is. Where the keys are is said by its line and lift, never its ground:
    // with them in the list, its line is a little darker than the hairlines
    // - a fifth of the way to the quiet text - and it is raised a little off
    // the list; with them elsewhere, it is a card like any other. A row
    // under the pointer lifts a little; a chosen one keeps its own.
    return cn(
      base,
      "[border-width:1px] [border-style:solid]",
      isActive && isFocusWithin
        ? "border-[color-mix(in_srgb,var(--border)_80%,var(--muted-foreground))]"
        : isActive || isEveryRowCard
          ? "border-border"
          : "border-transparent",
      isActive || isEveryRowCard ? "bg-background" : "bg-transparent",
      isActive && isFocusWithin
        ? "shadow-sm shadow-black/5"
        : "[box-shadow:none]",
      hasHoverSurface &&
        !isActive &&
        "[&:hover:not([data-selectable-row-hover='title']_*)]:border-border [&:hover:not([data-selectable-row-hover='title']_*)]:bg-background [&:hover:not([data-selectable-row-hover='title']_*)]:shadow-xs",
    );
  }
  // Flush there are no sides to draw: the hairlines above and below thicken
  // into the outlined border's colour and the band lifts onto the body.
  return cn(
    base,
    isActive
      ? "bg-muted [box-shadow:inset_0_1px_0_var(--border),inset_0_-1px_0_var(--border)]"
      : "bg-transparent [box-shadow:none]",
    hasHoverSurface &&
      "[&:hover:not([data-selectable-row-hover='title']_*)]:bg-muted",
  );
}
